import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Alert,
  Row,
  Col,
} from "react-bootstrap";
import {
  getTahapPelatihanList,
  createTahapPelatihan,
  updateTahapPelatihan,
  deleteTahapPelatihan,
} from "../../services/api";
import "./Dashboard.css";

const PAGE_SIZE = 10;

const statusBadge = (status) => {
  const map = {
    PENGAJUAN: "warning",
    DISETUJUI: "success",
    DIKEMBALIKAN: "danger",
    DITOLAK: "danger",
    SELESAI: "secondary",
  };
  return (
    <Badge bg={map[status] || "info"} text={map[status] === "warning" ? "dark" : undefined}>
      {status || "-"}
    </Badge>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const toInputDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return d.toISOString().split("T")[0];
};

const emptyForm = {
  tahun: new Date().getFullYear(),
  tglmulai: "",
  tglselesai: "",
  jumlah_peserta: "",
  file_permohonan: null,
};

const JadwalPelatihan = () => {
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [formData, setFormData] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [existingFileName, setExistingFileName] = useState("");

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTahapPelatihanList();
      if (res.data.success) {
        setDataList(res.data.data);
      }
    } catch (err) {
      setError("Gagal memuat data jadwal pelatihan.");
    } finally {
      setLoading(false);
    }
  };

  // Catatan dikembalikan — tampilkan alert jika ada record dengan status DIKEMBALIKAN
  const returnedRecords = dataList.filter(
    (d) => d.status === "DIKEMBALIKAN" && d.catatan,
  );

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "file_permohonan") {
      setFormData((prev) => ({ ...prev, file_permohonan: files[0] || null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setFormData({
      tahun: row.tahun || new Date().getFullYear(),
      tglmulai: toInputDate(row.tglmulai),
      tglselesai: toInputDate(row.tglselesai),
      jumlah_peserta: row.jumlah_peserta || "",
      file_permohonan: null,
    });
    setExistingFileName(row.file_permohonan || "");
    setSuccessMsg(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setFormData(emptyForm);
    setExistingFileName("");
    setError(null);
    setSuccessMsg(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!editId && !formData.file_permohonan) {
      setError("File permohonan wajib diupload untuk data baru.");
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("tahun", formData.tahun);
      fd.append("tglmulai", formData.tglmulai);
      fd.append("tglselesai", formData.tglselesai);
      fd.append("jumlah_peserta", formData.jumlah_peserta);
      if (formData.file_permohonan) {
        fd.append("file_permohonan", formData.file_permohonan);
      }

      if (editId) {
        await updateTahapPelatihan(editId, fd);
        setSuccessMsg("Data jadwal pelatihan berhasil diupdate.");
      } else {
        await createTahapPelatihan(fd);
        setSuccessMsg("Data jadwal pelatihan berhasil disimpan.");
      }

      handleCancelEdit();
      fetchData();
    } catch (err) {
      const msg =
        err.response?.data?.message || "Gagal menyimpan data. Coba lagi.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTahapPelatihan(deleteTarget.id);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      setSuccessMsg("Data berhasil dihapus.");
      fetchData();
    } catch (err) {
      setShowDeleteModal(false);
      setError("Gagal menghapus data.");
    }
  };

  // Pagination
  const totalPages = Math.ceil(dataList.length / PAGE_SIZE);
  const pagedData = dataList.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const fileDownloadUrl = (filename) =>
    filename
      ? `https://${process.env.REACT_APP_SIHALAL_HOSTNAME || "lembaga.halal.go.id"}/files/${filename}`
      : null;

  return (
    <div>
      {/* Page Header */}
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-calendar-plus"></i>
            </div>
            <div>
              <h1 className="page-header-title">Entri Jadwal Pelatihan</h1>
              <p className="page-header-subtitle">
                Kelola dan tambah jadwal pelatihan pendamping
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert returned records */}
      {returnedRecords.map((r) => (
        <Alert key={r.id} variant="danger" className="mt-2">
          <h6 className="alert-heading">
            <i className="fas fa-exclamation-triangle me-1"></i> Pengajuan
            Tahap {r.tahap} Tahun {r.tahun} Dikembalikan
          </h6>
          <strong>Catatan:</strong> {r.catatan}
          <hr />
          <small className="text-muted">
            Silakan perbaiki dan ajukan kembali.
          </small>
        </Alert>
      ))}

      {successMsg && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMsg(null)}
          className="mt-2"
        >
          <i className="fas fa-check-circle me-1"></i> {successMsg}
        </Alert>
      )}

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError(null)}
          className="mt-2"
        >
          <i className="fas fa-times-circle me-1"></i> {error}
        </Alert>
      )}

      {/* Form Card */}
      <Card className="mb-3">
        <Card.Header>
          <i className="fas fa-edit me-1"></i>
          {editId ? "Edit Jadwal Pelatihan" : "Tambah Jadwal Pelatihan"}
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-2 align-items-center">
              <Col md={2} className="fw-bold col-form-label">
                Tahun
              </Col>
              <Col md={3}>
                <Form.Control
                  type="number"
                  name="tahun"
                  size="sm"
                  value={formData.tahun}
                  onChange={handleChange}
                  required
                  min="2000"
                  max="2100"
                />
              </Col>
            </Row>
            <Row className="mb-2 align-items-center">
              <Col md={2} className="fw-bold col-form-label">
                Tanggal Mulai
              </Col>
              <Col md={3}>
                <Form.Control
                  type="date"
                  name="tglmulai"
                  size="sm"
                  value={formData.tglmulai}
                  onChange={handleChange}
                  required
                />
              </Col>
            </Row>
            <Row className="mb-2 align-items-center">
              <Col md={2} className="fw-bold col-form-label">
                Tanggal Selesai
              </Col>
              <Col md={3}>
                <Form.Control
                  type="date"
                  name="tglselesai"
                  size="sm"
                  value={formData.tglselesai}
                  onChange={handleChange}
                  required
                />
              </Col>
            </Row>
            <Row className="mb-2 align-items-center">
              <Col md={2} className="fw-bold col-form-label">
                Target Jumlah Peserta
              </Col>
              <Col md={3}>
                <Form.Control
                  type="number"
                  name="jumlah_peserta"
                  size="sm"
                  value={formData.jumlah_peserta}
                  onChange={handleChange}
                  min="0"
                />
              </Col>
            </Row>
            <Row className="mb-3 align-items-start">
              <Col md={2} className="fw-bold col-form-label">
                File Permohonan{" "}
                {!editId && <span className="text-danger">*</span>}
              </Col>
              <Col md={4}>
                <Form.Control
                  type="file"
                  name="file_permohonan"
                  size="sm"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleChange}
                  ref={fileInputRef}
                />
                <small className="text-muted">
                  Format: PDF, Word, Excel. Maks: 5MB
                </small>
                {editId && existingFileName && (
                  <div className="mt-1">
                    <small className="text-info">
                      <i className="fas fa-paperclip me-1"></i>
                      File saat ini: {existingFileName}
                      {" — "}
                      <em>kosongkan jika tidak ingin mengganti file</em>
                    </small>
                  </div>
                )}
              </Col>
            </Row>
            <div className="d-flex gap-2">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-1"></i>
                    {editId ? "Update Pelatihan" : "Simpan Pelatihan"}
                  </>
                )}
              </Button>
              {editId && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleCancelEdit}
                >
                  <i className="fas fa-times me-1"></i> Batal
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Data Table */}
      <Card>
        <Card.Body>
          <Alert variant="info" className="mb-3">
            <i className="fas fa-info-circle me-1"></i>
            <strong>Informasi Penting:</strong> Pengajuan jadwal pelatihan P3H
            melalui LMS harus{" "}
            <strong className="text-primary">DISETUJUI</strong> (lihat kolom
            status) terlebih dahulu oleh BPJPH sebelum jadwal tersebut dapat
            dipilih oleh calon peserta pelatihan.
          </Alert>

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table
                  bordered
                  hover
                  striped
                  size="sm"
                  className="align-middle"
                >
                  <thead className="table-light">
                    <tr>
                      <th>Tahap</th>
                      <th>Tahun</th>
                      <th>Tanggal Mulai</th>
                      <th>Tanggal Selesai</th>
                      <th>Jml. Peserta</th>
                      <th>Status</th>
                      <th>Catatan</th>
                      <th className="text-center">File</th>
                      <th className="text-center">Edit</th>
                      <th className="text-center">Hapus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedData.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center text-muted py-4">
                          Belum ada data jadwal pelatihan.
                        </td>
                      </tr>
                    ) : (
                      pagedData.map((row) => {
                        const isApproved = row.status === "DISETUJUI";
                        const downloadUrl = fileDownloadUrl(row.file_permohonan);
                        return (
                          <tr key={row.id}>
                            <td>{row.tahap}</td>
                            <td>{row.tahun}</td>
                            <td>{formatDate(row.tglmulai)}</td>
                            <td>{formatDate(row.tglselesai)}</td>
                            <td>{row.jumlah_peserta ?? "-"}</td>
                            <td>{statusBadge(row.status)}</td>
                            <td className="small">{row.catatan || ""}</td>
                            <td className="text-center">
                              {downloadUrl ? (
                                <a
                                  href={downloadUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Download File"
                                  style={{ color: "blue" }}
                                >
                                  <i className="fas fa-download"></i>
                                </a>
                              ) : (
                                <i
                                  className="fas fa-download text-muted"
                                  style={{ cursor: "not-allowed" }}
                                  title="File tidak tersedia"
                                ></i>
                              )}
                            </td>
                            <td className="text-center">
                              {isApproved ? (
                                <i
                                  className="fas fa-edit text-muted"
                                  style={{ cursor: "not-allowed" }}
                                  title="Data yang sudah disetujui tidak dapat diedit"
                                ></i>
                              ) : (
                                <button
                                  className="btn btn-link p-0"
                                  style={{ color: "green" }}
                                  title="Edit"
                                  onClick={() => handleEdit(row)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              )}
                            </td>
                            <td className="text-center">
                              {isApproved ? (
                                <i
                                  className="fas fa-trash text-muted"
                                  style={{ cursor: "not-allowed" }}
                                  title="Data yang sudah disetujui tidak dapat dihapus"
                                ></i>
                              ) : (
                                <button
                                  className="btn btn-link p-0"
                                  style={{ color: "red" }}
                                  title="Hapus"
                                  onClick={() => handleDeleteClick(row)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-2">
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage((p) => p - 1)}
                        >
                          &laquo;
                        </button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (p) => (
                          <li
                            key={p}
                            className={`page-item ${p === currentPage ? "active" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(p)}
                            >
                              {p}
                            </button>
                          </li>
                        ),
                      )}
                      <li
                        className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage((p) => p + 1)}
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal Konfirmasi Hapus */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="fas fa-trash me-1"></i> Konfirmasi Hapus
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Apakah Anda yakin ingin menghapus data Tahap{" "}
          <strong>{deleteTarget?.tahap}</strong> Tahun{" "}
          <strong>{deleteTarget?.tahun}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowDeleteModal(false)}
          >
            Batal
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteConfirm}>
            <i className="fas fa-trash me-1"></i> Ya, Hapus
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default JadwalPelatihan;

